import { useParams, Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { RecommendationCard } from '../components/RecommendationCard';
import type { PublicBusinessProfile } from '../types';

function setMetaTags(title: string, description: string, image?: string, url?: string) {
  document.title = title;
  
  const updateMeta = (name: string, content: string, property = false) => {
    const selector = property ? `meta[property="${name}"]` : `meta[name="${name}"]`;
    let meta = document.querySelector<HTMLMetaElement>(selector);
    if (!meta) {
      meta = document.createElement('meta');
      if (property) meta.setAttribute('property', name);
      else meta.setAttribute('name', name);
      document.head.appendChild(meta);
    }
    meta.setAttribute('content', content);
  };
  
  updateMeta('description', description);
  updateMeta('og:title', title, true);
  updateMeta('og:description', description, true);
  updateMeta('og:type', 'profile', true);
  if (url) updateMeta('og:url', url, true);
  if (image) updateMeta('og:image', image, true);
  updateMeta('twitter:card', 'summary_large_image');
  updateMeta('twitter:title', title);
  updateMeta('twitter:description', description);
  if (image) updateMeta('twitter:image', image);
}

function clearMetaTags() {
  document.title = 'Los Recomendados';
}

export function BusinessProfile() {
  const { id } = useParams<{ id: string }>();
  const [business, setBusiness] = useState<PublicBusinessProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;
    const fetch = async () => {
      try {
        const data = await window.discoveryApi.getBusinessProfile(id!);
        if (!mounted) return;
        if (!data) {
          setError('No se encontró el negocio');
        } else {
          setBusiness(data);
        }
      } catch (err) {
        if (mounted) setError(err instanceof Error ? err.message : 'Error al cargar');
      } finally {
        if (mounted) setLoading(false);
      }
    };
    fetch();
    return () => { mounted = false; };
  }, [id]);

  useEffect(() => {
    if (business) {
      const title = `${business.name} | Los Recomendados`;
      const description = business.description?.slice(0, 160) || `Descubre las recomendaciones de ${business.name} en Los Recomendados`;
      const image = business.cover?.url || business.logo?.url;
      const url = typeof window !== 'undefined' ? window.location.href : undefined;
      setMetaTags(title, description, image, url);
    }
    return () => clearMetaTags();
  }, [business, id]);

  if (loading) {
    return (
      <div className="container py-12">
        <div className="max-w-3xl mx-auto space-y-6">
          <div className="skeleton h-8 w-1/3" />
          <div className="skeleton h-48 w-full rounded-xl" />
          <div className="skeleton h-6 w-3/4" />
          <div className="skeleton h-4 w-full" />
        </div>
      </div>
    );
  }

  if (error || !business) {
    return (
      <div className="container py-20 text-center">
        <svg className="mx-auto h-16 w-16 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <h2 className="mt-4 text-xl font-semibold text-gray-900">No se encontró el negocio</h2>
        <p className="mt-2 text-gray-600">El negocio que buscas no existe o no está disponible públicamente.</p>
        <Link to="/" className="mt-6 inline-block btn-primary">
          Volver al inicio
        </Link>
      </div>
    );
  }

  return (
    <article className="min-h-screen">
      {/* Hero with cover image */}
      <div className="relative h-64 sm:h-80 lg:h-96 w-full">
        {business.cover && business.cover.url ? (
          <img
            src={business.cover.url}
            alt={business.name}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-primary/10 to-primary/20 flex items-center justify-center">
            <svg className="w-24 h-24 text-primary/50" fill="currentColor" viewBox="0 0 24 24">
              <path d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16l3-2 2 2 2-2 2 2 2-2 3 2z" />
            </svg>
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-6 pb-8">
          <div className="max-w-4xl mx-auto px-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              {business.logo && business.logo.url ? (
                <img
                  src={business.logo.url}
                  alt={business.name}
                  className="w-16 h-16 rounded-full object-cover border-2 border-white"
                />
              ) : (
                <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center">
                  <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16l3-2 2 2 2-2 2 2 2-2 3 2z" />
                  </svg>
                </div>
              )}
              <div>
                <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white">{business.name}</h1>
                {business.city && (
                  <p className="text-white/90 flex items-center gap-1">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    {business.city.name}
                  </p>
                )}
              </div>
            </div>
            <Link to="/" className="btn-secondary flex items-center gap-2 whitespace-nowrap">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              <span>Explorar</span>
            </Link>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container py-12">
        <div className="max-w-4xl mx-auto">
          {/* Description & Categories */}
          <div className="mb-12">
            {(business.description || business.categories.length > 0) && (
              <div className="mb-6">
                {business.description && (
                  <div className="whitespace-pre-wrap text-gray-700 leading-relaxed text-lg mb-6">
                    {business.description}
                  </div>
                )}
                {business.categories.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {business.categories.map((cat) => (
                      <span
                        key={cat.id}
                        className="px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium"
                      >
                        {cat.name}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Recommendations */}
          <div className="mb-8 flex items-center justify-between">
            <h2 className="text-2xl font-bold text-gray-900">
              Recomendaciones de {business.name}
            </h2>
            <span className="text-sm text-gray-500">
              {business.recommendationCount} recomendación{business.recommendationCount !== 1 ? 'es' : ''}
            </span>
          </div>

          {business.publishedRecommendations.length === 0 ? (
            <div className="text-center py-16">
              <svg className="mx-auto h-12 w-12 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16l3-2 2 2 2-2 2 2 2-2 3 2z" />
              </svg>
              <h3 className="mt-4 text-lg font-medium text-gray-900">Sin recomendaciones publicadas</h3>
              <p className="mt-2 text-gray-500">Este negocio aún no tiene recomendaciones públicas.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {business.publishedRecommendations.map((rec) => (
                <RecommendationCard key={rec.id} recommendation={rec} />
              ))}
            </div>
          )}
        </div>
      </div>
    </article>
  );
}